import React from "react"
import { makeStyles } from "@material-ui/styles"
import Config from "react-global-configuration"
import { Box, Hidden } from "@material-ui/core"
import { NavLink } from "react-router-dom"
import { ActionCard } from "./ActionCard"
import { useProcessUtils, toId, useDateUtils, useStringUtils } from "../../utils"
import { AdornmentIcon } from ".."
import { AuthorTools } from "../Author"
import { TemplateCategoryChip } from "../Chips"
import { RowBox } from "../Boxes"
import { FormatDateCompact, FormatScheduleTime } from "../Format"
import { JOB_PINNING, PROCESS_REPEAT } from "../../data"
import { ActionCardInfo } from "./ActionCardInfo"
import { Tooltip } from "../Tooltip"

const useStyles = makeStyles((theme) => ({
  info: {
    fontSize: 14,
    color: theme.palette.text.light,
    marginBottom: theme.spacing(1),
  },
  rightChildren: {
    height: 40,
    overflow: "visible",
  },
  locations: {
    fontWeight: "600",
    color: theme.palette.primary.main,
  },
  location: {
    fontWeight: "500",
  },
}))

const ProcessTemplateActionCard = ({
  process,
  set,
  category,
  onEdit,
  onCopy,
  onDisable,
  onDelete,
  onCategoryClick,
}) => {
  const { name, steps, inactive, locations, pinning } = process
  const classes = useStyles()
  const { formatTime, hasScheduleDate, scheduleDateToMoment } = useDateUtils()
  const { truncateInMiddle } = useStringUtils()
  const {
    getRepeatDisplayText,
    getRepeatDisplayShortText,
    getNavigateToTemplateLink,
    getProcessPinningTypeDescription,
  } = useProcessUtils()
  const { featuredCategory: featuredId } = Config.get("templates")

  const after = process.after ? scheduleDateToMoment(process.after) : null

  const handleEdit = () => {
    if (onEdit) onEdit(process)
  }
  const handleCopy = () => {
    if (onCopy) onCopy(process)
  }
  const handleDisable = () => {
    if (onDisable) onDisable(process)
  }
  const handleDelete = () => {
    if (onDelete) onDelete(process)
  }
  const handleCategoryClick = (id) => {
    if (onCategoryClick) onCategoryClick(id)
  }

  const statusPrefix = inactive ? "Inactive for" : `Active ${locations?.length > 1 ? "in" : "for"}`

  const pinningTypeDescription = getProcessPinningTypeDescription({ pinning })

  return (
    <ActionCard
      title={name}
      cardProps={{ component: NavLink, to: { pathname: getNavigateToTemplateLink(process, set, category, "view") } }}
      rightChildren={
        set !== "library" && (
          <Box
            mr="auto"
            display="flex"
            flexDirection="row"
            alignSelf="center"
            alignItems="center"
            className={classes.rightChildren}
          >
            <AuthorTools
              item={process}
              author={toId(process.author)}
              subject="process"
              admins={{ edit: "process_update_all", delete: "process_delete_all" }}
              disableLabel={inactive ? "Make active" : "Make inactive"}
              onEdit={handleEdit}
              onCopy={handleCopy}
              onDisable={handleDisable}
              onDelete={inactive && onDelete && handleDelete}
            />
          </Box>
        )
      }
      footer={
        <>
          <RowBox>
            <Hidden smUp>
              <Box mr={1}>
                <AdornmentIcon name="steps" tooltip={`${steps.length} steps`} />
              </Box>
            </Hidden>
            <Hidden xsDown>
              <AdornmentIcon name="steps" tooltip="Steps" />
              <Box ml={1} mr={5}>
                {steps.length} steps
              </Box>
            </Hidden>

            {!process.categories && (
              <>
                {process.repeat.type !== PROCESS_REPEAT.NEVER && (
                  <>
                    <Hidden smUp>
                      <ActionCardInfo>
                        <AdornmentIcon name="repeat" tooltip={<>Repeats: {getRepeatDisplayText(process)}</>} />
                      </ActionCardInfo>
                    </Hidden>
                    <Hidden xsDown>
                      <AdornmentIcon name="repeat" tooltip={`Repeats: ${getRepeatDisplayText(process)}`} />
                      <Box ml={1} mr={5} title={getRepeatDisplayText(process)}>
                        {getRepeatDisplayShortText(process)}
                      </Box>
                    </Hidden>
                  </>
                )}
                {process.repeat.type === PROCESS_REPEAT.NEVER && hasScheduleDate(after) && (
                  <>
                    <Hidden smUp>
                      <ActionCardInfo>
                        <AdornmentIcon
                          name="date"
                          tooltip={
                            <>
                              Repeats: <FormatDateCompact value={after} />
                            </>
                          }
                        />
                      </ActionCardInfo>
                    </Hidden>
                    <Hidden xsDown>
                      <AdornmentIcon name="date" tooltip="Repeats" />
                      <Box ml={1} mr={5}>
                        <FormatDateCompact value={after} />
                      </Box>
                    </Hidden>
                  </>
                )}

                {hasScheduleDate(process.due) && (
                  <>
                    <Hidden smUp>
                      <AdornmentIcon name="due" tooltip={<>Due: {formatTime(scheduleDateToMoment(process.due))}</>} />
                    </Hidden>
                    <Hidden xsDown>
                      <AdornmentIcon name="due" tooltip="Due" />
                      <Box ml={1} mr={5}>
                        <FormatScheduleTime value={process.due} />
                      </Box>
                    </Hidden>
                  </>
                )}
              </>
            )}
            {pinning?.type > JOB_PINNING.NONE && (
              <>
                <Hidden smUp>
                  <AdornmentIcon name="pinned" tooltip={pinningTypeDescription} />
                </Hidden>
                <Hidden xsDown>
                  <AdornmentIcon name="pinned" tooltip="Pinning" />
                  <RowBox ml={1} mr={5} justifySelf="center">
                    {pinningTypeDescription}
                  </RowBox>
                </Hidden>
              </>
            )}
          </RowBox>
          <RowBox ml="auto">
            {!process.categories && (
              <>
                {Boolean(locations.length) && (
                  <Box display="flex" alignItems="center" flexDirection="row">
                    <Box mr={0.5}>{statusPrefix}</Box>
                    {locations.length === 1 && (
                      <Box className={classes.location}>{truncateInMiddle(locations[0].name, 25)}</Box>
                    )}
                    {locations.length > 1 && (
                      <Tooltip title={locations.map((location) => location.name).join(", ")}>
                        <Box className={classes.locations}>
                          {locations.length} location{locations.length > 1 ? "s" : ""}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {!locations.length && <Box>{statusPrefix} 0 locations</Box>}
              </>
            )}
            {process.categories &&
              [...process.categories]
                .sort((a, b) => {
                  if (a.id === featuredId) return -1
                  if (b.id === featuredId) return 1
                  return a.name.localeCompare(b.name)
                })
                .map((item) => (
                  <Box key={item.id} ml={0.5}>
                    <TemplateCategoryChip category={item} onClick={handleCategoryClick} />
                  </Box>
                ))}
          </RowBox>
        </>
      }
    />
  )
}

export { ProcessTemplateActionCard }
